/**
 * ZIP mínimo, sem compressão (método STORE), montado no navegador.
 *
 * Existe porque o front não tinha biblioteca de zip (os zips do HUB eram todos
 * montados no backend) e a "Baixar assinados (.zip)" do dossiê da licitação é
 * frontend puro, por decisão da etapa 1. PDF já vem comprimido; comprimir de
 * novo ganharia quase nada e exigiria uma dependência.
 *
 * Formato: para cada arquivo, cabeçalho local + dados; no fim, o diretório
 * central e o registro de fim. Nomes em UTF-8 (bit 11 ligado), para os acentos
 * de "Declaração" chegarem certos no Windows e no macOS.
 */

const TABELA_CRC = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(dados: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < dados.length; i++) c = TABELA_CRC[(c ^ dados[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

export interface ArquivoDoZip {
  nome: string;
  dados: Uint8Array;
}

export function montarZip(arquivos: ArquivoDoZip[]): Uint8Array {
  const enc = new TextEncoder();
  const partes: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let deslocamento = 0;

  for (const a of arquivos) {
    const nome = enc.encode(a.nome);
    const crc = crc32(a.dados);
    const tam = a.dados.length;

    const local = new Uint8Array(30 + nome.length);
    const vl = new DataView(local.buffer);
    vl.setUint32(0, 0x04034b50, true); // assinatura do cabeçalho local
    vl.setUint16(4, 20, true); // versão necessária
    vl.setUint16(6, 0x0800, true); // bit 11: nome em UTF-8
    vl.setUint16(8, 0, true); // método 0 = STORE
    vl.setUint16(10, 0, true); // hora
    vl.setUint16(12, 0x21, true); // data (01/01/1980)
    vl.setUint32(14, crc, true);
    vl.setUint32(18, tam, true);
    vl.setUint32(22, tam, true);
    vl.setUint16(26, nome.length, true);
    vl.setUint16(28, 0, true);
    local.set(nome, 30);
    partes.push(local, a.dados);

    const cd = new Uint8Array(46 + nome.length);
    const vc = new DataView(cd.buffer);
    vc.setUint32(0, 0x02014b50, true); // assinatura do diretório central
    vc.setUint16(4, 20, true);
    vc.setUint16(6, 20, true);
    vc.setUint16(8, 0x0800, true);
    vc.setUint16(10, 0, true);
    vc.setUint16(12, 0, true);
    vc.setUint16(14, 0x21, true);
    vc.setUint32(16, crc, true);
    vc.setUint32(20, tam, true);
    vc.setUint32(24, tam, true);
    vc.setUint16(28, nome.length, true);
    vc.setUint32(42, deslocamento, true);
    cd.set(nome, 46);
    central.push(cd);

    deslocamento += local.length + tam;
  }

  const tamCentral = central.reduce((s, c) => s + c.length, 0);
  const fim = new Uint8Array(22);
  const vf = new DataView(fim.buffer);
  vf.setUint32(0, 0x06054b50, true); // fim do diretório central
  vf.setUint16(8, arquivos.length, true);
  vf.setUint16(10, arquivos.length, true);
  vf.setUint32(12, tamCentral, true);
  vf.setUint32(16, deslocamento, true);

  const total = deslocamento + tamCentral + fim.length;
  const out = new Uint8Array(total);
  let p = 0;
  for (const parte of [...partes, ...central, fim]) {
    out.set(parte, p);
    p += parte.length;
  }
  return out;
}
